import { appPath } from '@/lib/getPublicAppUrl'

// The mock uses different treatments for the same chain depending on context. The
// Browse pharmacies teaser, the Compare-pharmacies map marker, and the quick-filter
// chip all show a full-color circular badge (its own background baked in). The
// Compare-pharmacies list row instead shows a transparent wordmark crop laid over a
// neutral badge — Walmart included, via its own light-background mark, since its
// full-color badge's dark blue background doesn't suit that neutral-badge treatment.

const WORDMARK_ICON_BY_NAME: Record<string, string> = {
    cvs: appPath('/assets/pharmacy/cvs-wordmark.png'),
    walgreens: appPath('/assets/pharmacy/walgreens-wordmark.png'),
    lillydirect: appPath('/assets/pharmacy/lilly-direct-wordmark.svg'),
    amazonpharmacy: appPath('/assets/pharmacy/amazon-pharmacy-wordmark.webp'),
    novocare: appPath('/assets/pharmacy/novocare-wordmark.png'),
}

// Wordmarks used on the Compare-pharmacies list row and the pharmacy detail hero in place of
// a full-color badge. Walmart's full-color badge (see FULL_BADGE_ICON_BY_NAME) has its own
// dark blue background, which clashes with the neutral badge backing that transparent
// wordmarks sit on, so its light-background mark is used there instead. The other chains
// have no badge, only a wordmark.
const LIST_ROW_WORDMARK_ICON_BY_NAME: Record<string, string> = {
    walmart: appPath('/assets/pharmacy/walmart-wordmark.svg'),
    costco: appPath('/assets/pharmacy/costco-logo.png'),
    dillons: appPath('/assets/pharmacy/dillons-logo.svg'),
    homeland: appPath('/assets/pharmacy/homeland-logo.svg'),
    hyvee: appPath('/assets/pharmacy/hyvee-logo.png'),
    pricechopper: appPath('/assets/pharmacy/price-chopper-logo.png'),
    reasors: appPath('/assets/pharmacy/reasors-logo.png'),
    safeway: appPath('/assets/pharmacy/safeway-logo.jpeg'),
    schnucks: appPath('/assets/pharmacy/schnucks-logo.svg'),
}

// Self-contained circular badges (their own colored background) — used by the Browse
// pharmacies teaser, the Compare-pharmacies map marker, and the quick-filter chip.
const FULL_BADGE_ICON_BY_NAME: Record<string, string> = {
    cvs: appPath('/assets/pharmacy/cvs-logo.svg'),
    walgreens: appPath('/assets/pharmacy/walgreens-logo.svg'),
    walmart: appPath('/assets/pharmacy/walmart-logo.svg'),
    samsclub: appPath('/assets/pharmacy/sams-club-logo.jpeg'),
}

export const GENERIC_PHARMACY_ICON_SRC = appPath('/assets/pharmacy/generic-pharmacy-icon.svg')

function normalizeChainKey(chainName: string): string {
    return chainName.toLowerCase().replace(/[^a-z]/g, '')
}

/** Longest-first so "walgreens" wins over a hypothetical shorter prefix. */
const KNOWN_CHAIN_KEYS = Object.keys({ ...FULL_BADGE_ICON_BY_NAME, ...LIST_ROW_WORDMARK_ICON_BY_NAME }).sort(
    (left, right) => right.length - left.length,
)

/**
 * Resolves a known chain key from either a short chain name ("Walgreens") or a
 * store display name ("Walgreens Pharmacy #12345").
 */
export function resolvePharmacyChainKey(chainName: string): string | undefined {
    const normalized = normalizeChainKey(chainName)
    if (!normalized) return undefined
    if (FULL_BADGE_ICON_BY_NAME[normalized] || WORDMARK_ICON_BY_NAME[normalized]) {
        return normalized
    }
    return KNOWN_CHAIN_KEYS.find((key) => normalized.startsWith(key))
}

/** Full-color circular badge for the Browse pharmacies teaser and pharmacy detail
 *  hero, also used directly by the Compare-pharmacies map marker — Google Maps
 *  rasterizes marker icons onto an internal canvas, so they must be self-contained
 *  images rather than the list row's wordmark-on-neutral-badge composite. */
export function getTeaserIconSrc(chainName: string): string | undefined {
    const key = resolvePharmacyChainKey(chainName)
    return key ? FULL_BADGE_ICON_BY_NAME[key] : undefined
}

export interface ChainListIcon {
    src: string
    /** True when src is a transparent wordmark crop that needs a neutral circular
     *  backing; false when src already has its own background. */
    isWordmark: boolean
}

/** Icon for the Compare-pharmacies quick-filter chip, or undefined for an
 *  unrecognized chain (render the generic pharmacy icon instead). */
export function getChainListIcon(chainName: string): ChainListIcon | undefined {
    const key = resolvePharmacyChainKey(chainName)
    if (!key) return undefined
    if (WORDMARK_ICON_BY_NAME[key]) return { src: WORDMARK_ICON_BY_NAME[key], isWordmark: true }
    if (FULL_BADGE_ICON_BY_NAME[key]) return { src: FULL_BADGE_ICON_BY_NAME[key], isWordmark: false }
    return undefined
}

/** Icon for the Compare-pharmacies list row: identical to getChainListIcon, except
 *  Walmart uses its light-background wordmark (LIST_ROW_WORDMARK_ICON_BY_NAME)
 *  instead of the full-color badge used everywhere else. Undefined for an
 *  unrecognized chain (render the generic pharmacy icon instead). */
export function getListRowIcon(chainName: string): ChainListIcon | undefined {
    const key = resolvePharmacyChainKey(chainName)
    if (!key) return undefined
    const wordmark = LIST_ROW_WORDMARK_ICON_BY_NAME[key] ?? WORDMARK_ICON_BY_NAME[key]
    if (wordmark) return { src: wordmark, isWordmark: true }
    if (FULL_BADGE_ICON_BY_NAME[key]) return { src: FULL_BADGE_ICON_BY_NAME[key], isWordmark: false }
    return undefined
}

/** Icon for the pharmacy detail hero's circular badge: identical to getTeaserIconSrc's
 *  full-color badge for most chains, except Walmart, whose full badge's own blue
 *  background clashes with the hero's neutral badge — it uses the transparent wordmark
 *  instead, so the hero's own gray circle shows through. */
export function getHeroIcon(chainName: string): ChainListIcon | undefined {
    const key = resolvePharmacyChainKey(chainName)
    if (!key) return undefined
    if (LIST_ROW_WORDMARK_ICON_BY_NAME[key]) {
        return { src: LIST_ROW_WORDMARK_ICON_BY_NAME[key], isWordmark: true }
    }
    if (FULL_BADGE_ICON_BY_NAME[key]) return { src: FULL_BADGE_ICON_BY_NAME[key], isWordmark: false }
    return undefined
}
